import {Dispatch, GetState} from '../app-types';
import {AbortCategory, fetchJson, fetchWithBody, postJsonWithBody} from './fetcher';
import {showError} from '../message-box/message-box-actions';
import {HibuSearchRequest, HibuSearchResponse, ProdDocument} from '../search/search-types';
import {
    addBookmarkAction,
    doneSearchingAction,
    fetchNextPageCompletedAction,
    removeBookmarkAction,
    searchCompletedAction,
    searchHistoryClearedAction,
    searchHistoryLoadedAction,
    setUseAutoCorrection,
} from '../search/duck/search-actions';
import {
    BOOKMARKS_API_ENDPOINT,
    CLOSE_POINT_IN_TIME_ENDPOINT,
    FETCH_DOWNLOAD_FILE_API_ENDPOINT,
    FETCH_PREVIEW_DOCUMENT_API_ENDPOINT,
    FETCH_PREVIEW_FILE_API_ENDPOINT,
    formatHits,
    getActualQueryString,
    getSearchRequest,
    HIGHLIGHT_API_ENDPOINT,
    HISTORY_API_ENDPOINT,
    HTML_EXPORT,
    JSON_EXPORT,
    SAVED_SEARCH_API_ENDPOINT,
    SEARCH_DOCUMENTS_ENDPOINT,
    SEARCH_FACETS_ENDPOINT,
} from '../search/search.utils';
import {searchPageAttributesMap, SearchPageType} from '../search/search-config';
import {HistoryEntry} from '../search/duck/history-types';
import {SearchCompletedAction} from '../search/duck/search-types';
import {handleSearchError} from '../utils/utils';
import {changePendingState, setFacets} from '../facets/duck/filter.actions';

export const SEARCH_BATCH_SIZE = 24;

export function doClosePointInTime(pitId: string) {
    return (_dispatch: Dispatch): undefined | Promise<void> => {
        if (!pitId) {
            return;
        }
        return postJsonWithBody<void>(
            getApiUrl(SearchPageType.BASIC, CLOSE_POINT_IN_TIME_ENDPOINT),
            pitId,
            AbortCategory.CLOSE_PIT,
        ).catch(() => undefined);
    };
}

export function doFetchNextDocuments(requestBody: HibuSearchRequest) {
    return (dispatch: Dispatch): undefined | Promise<HibuSearchResponse | undefined> => {
        const searchDocumentsResponse = postJsonWithBody<HibuSearchResponse | undefined>(
            getApiUrl(SearchPageType.BASIC, SEARCH_DOCUMENTS_ENDPOINT),
            JSON.stringify(requestBody),
            AbortCategory.SEARCH,
        );

        return searchDocumentsResponse
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }
                return dispatch(fetchNextPageCompletedAction(formatHits(data.hits), data.pointInTime));
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            })
            .finally(() => dispatch(doneSearchingAction(SearchPageType.BASIC)));
    };
}

export function doFetchFacets(searchPageType: SearchPageType, requestBody: HibuSearchRequest) {
    return (dispatch: Dispatch): undefined | Promise<SearchCompletedAction | undefined> => {
        const searchDocumentsResponse = postJsonWithBody<HibuSearchResponse | undefined>(
            getApiUrl(searchPageType, SEARCH_FACETS_ENDPOINT),
            JSON.stringify(requestBody),
            AbortCategory.FACETS,
        );

        return searchDocumentsResponse
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }
                dispatch(setFacets(data.facets));
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            })
            .finally(() => {
                dispatch(changePendingState(false));
            });
    };
}

export function doSearchRequest(
    requestBody: HibuSearchRequest,
    isSubmittingNewQuery: boolean,
    shouldReplaceSearchResults: boolean,
    replaceUrl?: (searchExpression: string) => void,
) {
    return (dispatch: Dispatch, getState: GetState): undefined | Promise<SearchCompletedAction | undefined> => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        const state = getState();
        const {query, from, size} = requestBody;

        const searchState = state.search[searchPageType];
        const resultFor = searchState.resultFor;
        const spellCheckResult = searchState.spellCheckResult;
        const lastSearchedQuery = searchState.lastSearchedQuery;

        const searchDocumentsResponse = postJsonWithBody<HibuSearchResponse | undefined>(
            getApiUrl(searchPageType, SEARCH_DOCUMENTS_ENDPOINT),
            JSON.stringify(requestBody),
            AbortCategory.SEARCH,
        );
        return searchDocumentsResponse
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }

                let nextResultFor = resultFor || '';
                let nextSpellCheckResult = spellCheckResult || '';
                let lastSearched = lastSearchedQuery || '';
                if (isSubmittingNewQuery) {
                    nextResultFor = data.resultFor || '';
                    nextSpellCheckResult =
                        data.spellcheckSuggestions?.length > 0 ? data.spellcheckSuggestions[0].text : spellCheckResult;
                    lastSearched = data.searchRequest ? data.searchRequest.query : query;
                }
                if (replaceUrl) {
                    replaceUrl(nextResultFor);
                }
                return dispatch(
                    searchCompletedAction(
                        {
                            facets: data.facets,
                            docs: formatHits(data.hits),
                            totalFound: data.totalFound,
                            spellCheckResult: nextSpellCheckResult,
                            resultFor: nextResultFor,
                            lastSearchedQuery: lastSearched,
                            nextDocOffset: from + size,
                            shouldReplaceSearchResults: shouldReplaceSearchResults,
                            searchExecutionDurationMs: data.searchExecutionDurationMs,
                            pointInTime: data.pointInTime,
                        },
                        searchPageType,
                    ),
                );
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            })
            .finally(() => dispatch(doneSearchingAction(searchPageType)));
    };
}

export function doHTMLExport(type: SearchPageType) {
    return (dispatch: Dispatch, getState: GetState): void => {
        const state = getState();
        const query = getActualQueryString(state.search[type]);

        const request = getSearchRequest(getState, query);

        const reportEndpoint = getApiUrl(type, HTML_EXPORT);
        const response = postJsonWithBody(reportEndpoint, JSON.stringify(request));

        response
            .then((htmlAsString) => {
                const newTab = window.open('', '_blank');
                if (newTab != null && typeof htmlAsString === 'string') {
                    newTab.document.open();
                    newTab.document.write(htmlAsString);
                    newTab.document.close();
                }
            })
            .catch((error) => {
                console.error(error);
                return dispatch(showError(type, 'search.export.error.general.html'));
            });
    };
}

export function doJsonExport(type: SearchPageType) {
    return (dispatch: Dispatch, getState: GetState): void => {
        const state = getState();
        const query = getActualQueryString(state.search[type]);

        const request = getSearchRequest(getState, query);
        dispatch(setUseAutoCorrection(false));

        const reportEndpoint = getApiUrl(type, JSON_EXPORT);
        const response = postJsonWithBody(reportEndpoint, JSON.stringify(request));

        response
            .then((response) => {
                const jsonBlob = new Blob([JSON.stringify(response)], {type: 'application/json'});

                const anchor = document.createElement('a');
                anchor.style.display = 'none';
                anchor.href = URL.createObjectURL(jsonBlob);
                anchor.download = 'data.json';
                document.body.appendChild(anchor);
                anchor.click();

                // Clean up the anchor element and URL object
                return () => {
                    anchor.remove();
                    URL.revokeObjectURL(anchor.href);
                };
            })
            .catch((error) => {
                console.error(error);
                return dispatch(showError(type, 'search.export.error.general.json'));
            });
    };
}

export const addBookmark = (type: SearchPageType, doc: ProdDocument) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        dispatch(addBookmarkAction(type, doc));
        const containerName = getState().settings.containerName;
        const bookmark = {
            container: containerName,
            documentId: doc.id,
            timestamp: Date.now(),
        };

        fetchWithBody(getBookmarksApiUrl(type), 'POST', JSON.stringify(bookmark))
            .catch(() => {
                dispatch(showError(type, 'search.error.favorites'));
                dispatch(removeBookmarkAction(type, doc));
            });
    };
};

export const removeBookmark = (type: SearchPageType, doc: ProdDocument) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        dispatch(removeBookmarkAction(type, doc));
        const containerName = getState().settings.containerName;
        const bookmark = {
            container: containerName,
            documentId: doc.id,
        };

        fetchWithBody(getBookmarksApiUrl(type), 'DELETE', JSON.stringify(bookmark)).catch(() => {
            dispatch(showError(type, 'Bookmark could not be removed.'));
            dispatch(addBookmarkAction(type, doc));
        });
    };
};

export const fetchHistory = (searchPageType: SearchPageType) => {
    return (dispatch: Dispatch): void => {
        fetchJson<HistoryEntry[]>(getHistoryApiUrl(searchPageType), 'GET')
            .then((data) => {
                dispatch(searchHistoryLoadedAction(searchPageType, data));
            })
            .catch(() => {
                return dispatch(showError(searchPageType, 'search.error.historyLoad'));
            });
    };
};

export const clearHistory = (searchPageType: SearchPageType) => {
    return (dispatch: Dispatch): void => {
        fetchJson<void>(getHistoryApiUrl(searchPageType), 'DELETE')
            .then(() => {
                dispatch(searchHistoryClearedAction(searchPageType));
            })
            .catch(() => {
                return dispatch(showError(searchPageType, 'search.error.historyLoad'));
            });
    };
};

export function getHighlightApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, HIGHLIGHT_API_ENDPOINT);
}

function getBookmarksApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, BOOKMARKS_API_ENDPOINT);
}

function getHistoryApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, HISTORY_API_ENDPOINT);
}

export function getSavedSearchApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, SAVED_SEARCH_API_ENDPOINT);
}

export function getDocumentPreviewApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, FETCH_PREVIEW_DOCUMENT_API_ENDPOINT);
}

export function getFileDownloadApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, FETCH_DOWNLOAD_FILE_API_ENDPOINT);
}

export function getFilePreviewApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, FETCH_PREVIEW_FILE_API_ENDPOINT);
}

function getApiUrl(searchPageType: SearchPageType, endpoint: string): string {
    return searchPageAttributesMap[searchPageType].endpoint + '/' + endpoint;
}
